import React, { useState } from 'react';

interface QuizQuestion {
  question: string;
  options: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correct_answer: string;
  explanation: string;
}

interface QuizData {
  topic: string;
  difficulty: string;
  questions: QuizQuestion[];
}

interface QuizProps {
  quiz: QuizData;
}

const Quiz: React.FC<QuizProps> = ({ quiz }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: string }>({});
  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState<{ [key: number]: boolean }>({});

  const handleAnswerSelect = (questionIndex: number, answer: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: answer
    });
  };

  const handleShowExplanation = (questionIndex: number) => {
    setShowExplanation({
      ...showExplanation,
      [questionIndex]: true
    });
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    setShowResults(true);
  };

  const calculateScore = () => {
    let correct = 0;
    quiz.questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correct_answer) {
        correct++;
      }
    });
    return correct;
  };

  const getScoreColor = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return '#4caf50'; // Green
    if (percentage >= 60) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const question = quiz.questions[currentQuestion];
  const isAnswered = selectedAnswers[currentQuestion] !== undefined;
  const isCorrect = selectedAnswers[currentQuestion] === question.correct_answer;
  const allAnswered = quiz.questions.every((_, index) => selectedAnswers[index] !== undefined);

  if (showResults) {
    const score = calculateScore();
    const total = quiz.questions.length;
    const percentage = Math.round((score / total) * 100);

    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>📊 Quiz Results</h3>
          <div className="quiz-topic">
            <strong>{quiz.topic}</strong> • {quiz.difficulty}
          </div>
        </div>

        <div className="quiz-results">
          <div className="score-circle" style={{ borderColor: getScoreColor(score, total) }}>
            <div className="score-text" style={{ color: getScoreColor(score, total) }}>
              {score}/{total}
            </div>
            <div className="score-percentage">{percentage}%</div>
          </div>

          <div className="results-summary">
            {percentage >= 80 && <p>🎉 Excellent work! You have a strong understanding of {quiz.topic}.</p>}
            {percentage >= 60 && percentage < 80 && <p>👍 Good job! You're on the right track with {quiz.topic}.</p>}
            {percentage < 60 && <p>📚 Keep learning! Review the explanations below to strengthen your understanding.</p>}
          </div>

          <div className="results-details">
            <h4>Review Your Answers</h4>
            {quiz.questions.map((q, index) => {
              const userAnswer = selectedAnswers[index];
              const isCorrect = userAnswer === q.correct_answer;

              return (
                <div key={index} className={`result-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="result-header">
                    <span className="result-icon">{isCorrect ? '✅' : '❌'}</span>
                    <span className="result-number">Question {index + 1}</span>
                  </div>
                  <p className="result-question">{q.question}</p>
                  <div className="result-answers">
                    <p>
                      <strong>Your answer:</strong> {userAnswer} - {q.options[userAnswer as keyof typeof q.options]}
                    </p>
                    {!isCorrect && (
                      <p>
                        <strong>Correct answer:</strong> {q.correct_answer} - {q.options[q.correct_answer as keyof typeof q.options]}
                      </p>
                    )}
                  </div>
                  <div className="result-explanation">
                    <strong>Explanation:</strong> {q.explanation}
                  </div>
                </div>
              );
            })}
          </div>

          <button className="quiz-button" onClick={() => {
            setShowResults(false);
            setCurrentQuestion(0);
            setSelectedAnswers({});
            setShowExplanation({});
          }}>
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <h3>📝 Quiz: {quiz.topic}</h3>
        <div className="quiz-meta">
          <span className="quiz-difficulty">{quiz.difficulty}</span>
          <span className="quiz-progress">
            Question {currentQuestion + 1} of {quiz.questions.length}
          </span>
        </div>
      </div>

      <div className="quiz-content">
        <div className="quiz-question">
          <p>{question.question}</p>
        </div>

        <div className="quiz-options">
          {Object.entries(question.options).map(([key, value]) => {
            const isSelected = selectedAnswers[currentQuestion] === key;
            const showCorrect = showExplanation[currentQuestion] && key === question.correct_answer;
            const showIncorrect = showExplanation[currentQuestion] && isSelected && key !== question.correct_answer;

            return (
              <button
                key={key}
                className={`quiz-option ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                onClick={() => handleAnswerSelect(currentQuestion, key)}
                disabled={showExplanation[currentQuestion]}
              >
                <span className="option-letter">{key}</span>
                <span className="option-text">{value}</span>
                {showCorrect && <span className="option-icon">✓</span>}
                {showIncorrect && <span className="option-icon">✗</span>}
              </button>
            );
          })}
        </div>

        {isAnswered && !showExplanation[currentQuestion] && (
          <button
            className="quiz-button check-answer"
            onClick={() => handleShowExplanation(currentQuestion)}
          >
            Check Answer
          </button>
        )}

        {showExplanation[currentQuestion] && (
          <div className={`quiz-explanation ${isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="explanation-header">
              {isCorrect ? (
                <>
                  <span className="explanation-icon">✅</span>
                  <strong>Correct!</strong>
                </>
              ) : (
                <>
                  <span className="explanation-icon">❌</span>
                  <strong>Incorrect</strong>
                </>
              )}
            </div>
            <p>{question.explanation}</p>
          </div>
        )}

        <div className="quiz-navigation">
          <button
            className="quiz-button secondary"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            ← Previous
          </button>

          {currentQuestion < quiz.questions.length - 1 ? (
            <button
              className="quiz-button"
              onClick={handleNext}
            >
              Next →
            </button>
          ) : (
            <button
              className="quiz-button primary"
              onClick={handleSubmit}
              disabled={!allAnswered}
            >
              Submit Quiz
            </button>
          )}
        </div>

        <div className="quiz-progress-bar">
          <div
            className="quiz-progress-fill"
            style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default Quiz;
